import React from 'react';
import classnames from 'classnames';
import { Form, FormField, FormProps, FormGroup, validateForm, ValidationOptions } from '@gik/ui/Form';
import BillingFormSchema from './BillingFormSchema';
import { Input, Button } from '@gik/ui';
import { Source, StripeError } from '@stripe/stripe-js';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripeCardElementOptions, withStripe } from '@gik/ui/Stripe';
import { FormApi, SubmissionErrors } from 'final-form';
import { gikClassPrefix } from '@gik/core/constants';
import { BillingDetails, CartItem, ShippingDetails } from '@gik/checkout/types';
import CreditCardImages from './CreditCardImages';
import { ICheckoutFormModalContext, CheckoutFormModalContext } from '../CheckoutForm/CheckoutFormModal';
import { StripeCardElement } from '@stripe/stripe-js';
import { useUserStore } from '@gik/core/store/UserStore';
import i18n from '@gik/i18n';
import { translationKeys } from '../../i18n/en';

export interface BillingFormValues extends BillingDetails {
  stripeToken?: string;
}

const blockName = `${gikClassPrefix}-billing-form`;

export interface BillingFormProps extends FormProps {
  initialValues?: Partial<BillingFormValues>;
  initialOrders?: CartItem[];
  shippingDetails?: ShippingDetails;

  /**
   * This is used when a stripe token is already available and an
   * obfuscated card number should be displayed
   */
  creditCardLast4?: string;

  setCardElement?(cardElement: StripeCardElement): void;

  onBeforeSubmit?: (
    values: object,
    form: FormApi<object, object>
  ) => void | SubmissionErrors | Promise<SubmissionErrors>;

  onAfterSubmit?: (
    values: object,
    form: FormApi<object, object>,
    source: Source
  ) => void | SubmissionErrors | Promise<SubmissionErrors>;
}

export function createStripeSource(values, stripe, cardElement) {
  return stripe.createSource(cardElement, {
    type: 'card',
    // pass billing details to stripe
    currency: 'usd',
    owner: {
      address: {
        city: values.city,
        country: values.country,
        line1: values.address1,
        line2: values.address2,
        postal_code: values.postalCode,
        state: values.state,
      },
      email: values.email,
      name: values.firstName + ' ' + values.lastName,
      phone: values.phone,
    },
  });
}

function BillingForm({
  initialValues,
  initialOrders,
  shippingDetails,
  creditCardLast4,
  submitButton,
  className,
  setCardElement,
  onSubmit,
  onBeforeSubmit,
  onAfterSubmit,
  ...otherProps
}: BillingFormProps): React.ReactElement {
  const [stripeError, setStripeError] = React.useState<StripeError>(undefined);
  const [stripeEditable, setStripeEditable] = React.useState<Boolean>(false);
  const userId = useUserStore(state => state.id);

  const context = React.useContext<ICheckoutFormModalContext>(CheckoutFormModalContext);

  const stripe = useStripe();
  const elements = useElements();

  const useStripeInput = !initialValues.stripeToken || stripeEditable;

  async function handleSubmit(values: BillingFormValues, form: FormApi<object, object>): Promise<void> {
    setStripeError(undefined);
    if (onBeforeSubmit) onBeforeSubmit(values, form);

    let stripeSource: Source;

    if (useStripeInput) {
      const cardElement = elements.getElement(CardElement);

      setCardElement(cardElement);

      const { error, source } = await createStripeSource(values, stripe, cardElement);

      stripeSource = source;

      if (error) {
        setStripeError(error);
        return;
      } else {
        // add the stripeToken to the values object
        values.stripeToken = stripeSource.id;
      }
    } else {
      // add the stripeToken from initialValues
      values.stripeToken = initialValues.stripeToken;
    }

    if (onSubmit) onSubmit(values, form);

    if (onAfterSubmit) onAfterSubmit(values, form, stripeSource);
  }

  function handleChangeStripe() {
    setStripeEditable(true);
  }

  const schema = BillingFormSchema(context?.modalContentRef, !!userId);

  const blockClasses = classnames([blockName, className || '']);

  function validate(values: BillingFormValues, opts: ValidationOptions) {
    const validation = validateForm(schema, values, opts);

    const cannotSendToSelf =
      initialOrders?.some(order => order.checkoutType === 'tango-card') &&
      values.email &&
      values.email === shippingDetails?.email;

    if (cannotSendToSelf) {
      validation.email = {
        message: i18n.t(translationKeys.tangoCardCannotPurchaseForSelf),
        // TODO: 'singleRequired' is not really the correct type for this validation error
        type: 'singleRequired',
      };
    }

    return validation;
  }

  return (
    <Form
      {...otherProps}
      onSubmit={handleSubmit}
      schema={schema}
      initialValues={initialValues}
      vertical
      validateOnBlur={false}
      className={blockClasses}
      validate={validate}
      render={({ submitting, valid }) => {
        return (
          <>
            <section>
              <FormField name="firstName" className="tw-flex-1" />
              <FormField name="lastName" className="tw-flex-1" />
            </section>
            <FormField name="companyName" />
            <FormField name="country" />
            <FormField name="address1" />
            <FormField name="address2" />
            <section>
              <FormField name="city" className="tw-flex-1" />
              <FormField name="state" />
              <FormField name="postalCode" />
            </section>
            <section>
              <FormField name="phone" className="tw-flex-1" />
              <FormField name="email" className="tw-flex-1" />
            </section>
            <br />
            <strong className="tw-text-sm">Credit Card (Stripe)</strong>&nbsp;
            <CreditCardImages />
            <FormGroup vertical label={<strong>Credit or debit card</strong>} className="gik-form-group--highlight">
              {/*
                NOTE: this input should always be rendered and mounted to be able to recreate a new stripe source multiple times (in case initial payment failed)
                Since we are not attaching payment sources to customer objects yet we need to recreate a single use source for every attempt.
              */}
              <Input
                className={classnames({ 'gik-input--hidden': !useStripeInput })}
                customInput={customProps => <CardElement options={stripeCardElementOptions} {...customProps} />}
              />

              {!useStripeInput && (
                <div>
                  xxxx-xxxx-xxxx-{creditCardLast4} (
                  <Button variant="primary-link" onClick={handleChangeStripe}>
                    Change
                  </Button>
                  )
                </div>
              )}
            </FormGroup>
            {stripeError && <div className="gik-form__error">{stripeError.message}</div>}
            {submitButton && (
              <div className="tw-text-center tw-pt-4">
                <Button variant="primary" type="submit" loading={submitting} disabled={!valid} wide>
                  CONTINUE
                </Button>
              </div>
            )}
          </>
        );
      }}
    />
  );
}

export default withStripe(BillingForm);
